
// 从 controlTypes.ts 导入控件类型
import type {
  BorderStyle,
  ControlType,
  BaseControlProps,
  TextStyleProps,
  ContainerStyleProps,
  TextControl,
  MultilineTextControl,
  FieldControl,
  ImageControl,
  LineControl,
  RectangleControl,
  PageNumberControl,
  CurrentDateControl,
  BarcodeControl,
  EllipseControl,
  ControlObject,
  ControlObjectAll,
  ControlTypeConfig,
} from './controlTypes';

// 导出类型守卫函数
export {
  isTextStyleControl,
  isContainerStyleControl,
  hasBorderControl,
  hasFieldBinding,
  getTextStyleProps,
  getContainerStyleProps,
} from './controlTypes';

// 重新导出控件类型
export type {
  BorderStyle,
  ControlType,
  BaseControlProps,
  TextStyleProps,
  ContainerStyleProps,
  TextControl,
  MultilineTextControl,
  FieldControl,
  ImageControl,
  LineControl,
  RectangleControl,
  PageNumberControl,
  CurrentDateControl,
  BarcodeControl,
  EllipseControl,
  ControlObject,
  ControlObjectAll,
  ControlTypeConfig,
};


export interface Band {
    id: 'header' | 'detail' | 'summary' | 'footer';
     name: string;
     type: string;
     top: number;
     bottom: number;
     actualBottom: number;
     visible: boolean;
     objects: ControlObject[];
     rowCount?: number; // 明细带行数
     rowHeight?: number; // 明细带单行高度
     offsetY?: number; // 用于记录拖动时的偏移量
     backgroundColor?: string; // 带区背景色
     backgroundColorFormula?: string; // 背景色公式（仅明细/汇总带区，返回颜色值）
     rowHeightFormula?: string; // 行高公式（仅明细/汇总带区，返回数值）
    [key: string]: any;
}



export interface DesignerOptions {
  minBandHeight: number;
  defaultBandHeight: number;
  showGrid: boolean;
  gridSize: number;
  bandSpacing: number;
}

export interface DesignerState {
    draggingBoundary: string | null;
    selectedBand: string | null;
    selectedObject: string | null;
    showBands: boolean;
    showGrid: boolean;
    showGuides: boolean;
    zoomLevel: number;
    showRulers: boolean;
    showPageMargins: boolean;
}

export interface DataField {
    name: string;
    label: string;
    type: 'string' | 'number' | 'currency' | 'date' | 'array';
    /** 子字段（用于 array 类型） */
    children?: DataField[];
}

/** 扁平化的字段信息（用于 UI 显示） */
export interface FlatDataField {
    name: string;
    label: string;
    type: 'string' | 'number' | 'currency' | 'date';
    /** 数据来源: master-主表字段, detail-明细字段 */
    source: 'master' | 'detail';
    /** 所属数组的 key（仅明细字段有值） */
    arrayKey?: string;
    /** 所属数组的显示名称（仅明细字段有值） */
    arrayLabel?: string;
}

/**
 * 将嵌套的 DataField 结构展开为扁平列表
 * @param dataFields 嵌套的数据字段定义
 * @param parentName 父字段名称（用于生成路径如 products.name）
 * @returns 扁平化的字段列表，包含 source 和 arrayKey 标记
 */
export function flattenDataFields(dataFields: DataField[], parentName?: string): FlatDataField[] {
    const result: FlatDataField[] = [];
    for (const field of dataFields) {
        if (field.type === 'array' && field.children) {
            // 递归处理子字段，标记为 detail
            for (const child of field.children) {
                const childName = `${field.name}.${child.name}`;
                if (child.type === 'array' && child.children) {
                    // 支持多层嵌套
                    result.push(...flattenDataFields([child], field.name));
                } else {
                    result.push({
                        name: childName,
                        label: child.label,
                        type: child.type as 'string' | 'number' | 'currency' | 'date',
                        source: 'detail',
                        arrayKey: field.name,
                        arrayLabel: field.label
                    });
                }
            }
        } else {
            // 主表字段
            result.push({
                name: parentName ? `${parentName}.${field.name}` : field.name,
                label: field.label,
                type: field.type as 'string' | 'number' | 'currency' | 'date',
                source: parentName ? 'detail' : 'master'
            });
        }
    }
    return result;
}

/** 明细数组信息 */
export interface DetailArrayInfo {
    key: string;
    label: string;
}

/**
 * 获取所有明细数组的信息
 * @param dataFields 数据字段定义
 * @returns 明细数组信息列表
 */
export function getDetailArrays(dataFields: DataField[]): DetailArrayInfo[] {
    return dataFields
        .filter(f => f.type === 'array')
        .map(f => ({ key: f.name, label: f.label }));
}

/**
 * 按明细数组分组字段
 * @param flatFields 扁平化的字段列表
 * @returns 分组后的字段，包含主表字段和各明细数组字段
 */
export function groupFieldsByArray(flatFields: FlatDataField[]): {
    master: FlatDataField[];
    details: Record<string, { label: string; fields: FlatDataField[] }>;
} {
    const master = flatFields.filter(f => f.source === 'master');
    const details: Record<string, { label: string; fields: FlatDataField[] }> = {};
    
    for (const field of flatFields) {
        if (field.source === 'detail' && field.arrayKey) {
            if (!details[field.arrayKey]) {
                details[field.arrayKey] = {
                    label: field.arrayLabel || field.arrayKey,
                    fields: []
                };
            }
            details[field.arrayKey].fields.push(field);
        }
    }
    
    return { master, details };
}

// 页面设置类型
export interface PageSettings {
    /** 纸张类型 */
    paperSize: 'A4' | 'A3' | 'A5' | 'B4' | 'B5' | 'Letter' | 'Legal' | 'Custom';
    /** 纸张宽度（mm 或 inch，根据 unit） */
    width: number;
    /** 纸张高度（mm 或 inch，根据 unit） */
    height: number;
    /** 单位 */
    unit: 'mm' | 'in';
    /** 页边距 */
    margins: {
        top: number;
        bottom: number;
        left: number;
        right: number;
    };
    /** 纸张方向 */
    orientation: 'portrait' | 'landscape';
}

/** 设计数据（统一的 JSON 结构） */
export interface DesignData {
    /** 带区设计数据（可选，为空时使用默认带区） */
    bands?: Band[];
    /** 页面设置（可选，为空时使用默认 A4 设置） */
    pageSettings?: PageSettings;
}

export interface BandBoundaryDesignerProps {
    options?: Partial<DesignerOptions>;
    initialDesign?: Band[];
    onDesignChange?: (bands: Band[]) => void;
    onSave?: (design: any) => void;
    onPreview?: () => void;
    /** 预览数据源 */
    data?: Record<string, any>;
    dataFields: DataField[];
    /** 初始页面设置 */
    initialPageSettings?: PageSettings;
    /** 页面设置变更回调 */
    onPageSettingsChange?: (settings: PageSettings) => void;
}